import React, { useState, useRef, useEffect, useMemo } from "react";
import "../assets/styles/chatSection.scss"
import { message, Modal, Radio, Spin, Tooltip, Dropdown, Menu } from "antd";
import UserIcon from "../assets/images/27470347_7309691 1.svg";
import BotIcon from "../assets/images/1100838311-preview 1.svg";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import { autoCoderChatBotTemplateAPI } from "../apis";
import { sendAutoCoderConversation, getAutoCoderErrorMessage, getEmptyModelResponseMessage } from "../apis/autoCoderConversation";
import {
  buildConversationPayload,
  getAutoCoderModel,
  getAutoCoderRole,
} from "../pages/Auto-Coder/autoCoderConfig";
import { CopyOutlined, FilePdfOutlined, StarOutlined, InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import { Auth } from "../libs/utilities/helpers/Auth";
import mammoth from 'mammoth';
import SafeMarkdown from "./SafeMarkdown";
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
// import jsPDF from "jspdf";
// import { marked } from "marked"
// import html2canvas from "html2canvas";
// import html2pdf from "html2pdf.js";

const AutoCoderChatSection = ({
  inputValue,
  selectedFile,
  folderList,
  feature,
  generationSettings,
  setChatLoad,
  serviceId,
  registerControls,
  onStateChange,
}) => {
  const dispatch = useDispatch();
  const [chatHistory, setChatHistory] = useState([]);
  const chatContentRef = useRef(null);
  const messagesRef = useRef([]);
  const [selectedPDF, setSelectedPDF] = useState("");
  const [pdfLoading, setPDFLoading] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [ratings, setRatings] = useState({});
  const [dislikeModal, setDislikeModal] = useState({
    visible: false,
    messageId: null,
    starRating: null,
  });
  const [selectedOption, setSelectedOption] = useState(null);
  const [dislikeFeedback, setDislikeFeedback] = useState("");
  const dislikeInputRef = useRef(null);
  const user = Auth.getUser();
  // const markdownRef = useRef();
  const markdownRefs = useRef({});
  const promiseRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastRequestRef = useRef(null);
  const [isRequestActive, setIsRequestActive] = useState(false);
  const activeMessageIdRef = useRef(null);

  const settings = generationSettings || {
    modelId: "qwen-text",
    roleId: "developer",
    parameters: { temperature: 0.6, max_tokens: 5000 },
  };

  const updateChatMessage = (messageId, patch) => {
    setChatHistory((prev) =>
      prev.map((item) => (item.uuid === messageId ? { ...item, ...patch } : item))
    );
  };

  const buildResponseMeta = (result) => {
    const modelConfig = getAutoCoderModel(settings.modelId);
    const roleConfig = getAutoCoderRole(settings.roleId);
    const resolvedModel = getAutoCoderModel(result.model || settings.modelId);

    return {
      model: result.model || modelConfig.id,
      modelLabel: resolvedModel.label,
      roleLabel: roleConfig.label,
      timeTakenMs: result.timeTakenMs,
      latencyMs: result.latencyMs,
      promptTokens: result.promptTokens,
      completionTokens: result.completionTokens,
      totalTokens: result.totalTokens,
      requestId: result.requestId,
    };
  };

  //useEffect to adjust scroll to top and bottom
  useEffect(() => {
    const lastMessage = messagesRef.current[chatHistory.length - 1];
    if (chatContentRef.current && lastMessage) {
      const chatBox = chatContentRef.current;
      const lastMessageRect = lastMessage.getBoundingClientRect();
      const chatBoxRect = chatBox.getBoundingClientRect();
      // Check if last message is not fully visible
      if (lastMessageRect.bottom > chatBoxRect.bottom) {
        lastMessage.scrollIntoView({
          behavior: "smooth",
          block: "start", // Scroll just enough to show the message
        });
      }
    }

  }, [chatHistory]);

  useEffect(() => {
    if (registerControls) {
      registerControls({
        stop: handleStop,
        restart: handleRestart,
        getIsActive: () => isRequestActive,
        isActive: isRequestActive,
      });
    }
  }, [isRequestActive]);

  //useEffect to take a input value from the gpt template and render the response
  useEffect(() => {
    if (inputValue) {
      handleChatSubmit(inputValue);
    }
  }, [inputValue])

  const getFileName = (fileId) => {
    const file = folderList?.find(item => item.file?.find(file => file?.id === fileId))?.file?.find(item => item.id === fileId);
    return file?.name;
  }

  // const runRequest = async (requestBody, messageId) => {
  //   setIsRequestActive(true);

  //   try {
  //     const promise = dispatch(
  //       autoCoderChatBotTemplateAPI.generateAnswer(requestBody)
  //     );

  //     promiseRef.current = promise;

  //     const response = await promise;

  //     setChatHistory((prev) => {
  //       return prev.map((item) =>
  //         item.uuid === messageId
  //           ? {
  //             ...item,
  //             response: response.payload.data.answer,
  //             isLoading: false,
  //           }
  //           : item
  //       );
  //     });
  //   } catch (error) {
  //     setChatHistory((prev) =>
  //       prev.map((item) =>
  //         item.uuid === messageId
  //           ? {
  //             ...item,
  //             response:
  //               error?.name === "AbortError"
  //                 ? "Stopped"
  //                 : "Something went wrong",
  //             isLoading: false,
  //           }
  //           : item
  //       )
  //     );
  //   } finally {
  //     setIsRequestActive(false);
  //     promiseRef.current = null;
  //   }
  // };

  useEffect(() => {
    if (!onStateChange) return;

    const lastMessage = chatHistory[chatHistory.length - 1];

    onStateChange({
      isRunning: lastMessage?.isLoading,
      showButton: !!lastMessage?.showControlButton,
    });
  }, [chatHistory]);

  const executeConversation = async (requestBody, messageId) => {
    abortControllerRef.current = new AbortController();
    setIsRequestActive(true);

    const conversationBody = buildConversationPayload({
      query: requestBody.query,
      queryId: requestBody.query_id,
      fileIds: requestBody.file_id,
      generationSettings: settings,
    });

    try {
      const result = await sendAutoCoderConversation({
        body: conversationBody,
        signal: abortControllerRef.current.signal,
        fallbackModelId: settings.modelId,
      });


      // const answer = (result.answer || "").replace(/^\n+/, "");
      const answer = (result.answer || "").replace(/^\n+/, "");

      if (!answer) {
        // throw new Error("Invalid response");
        const modelId = result.model || settings.modelId;
        throw Object.assign(new Error(getEmptyModelResponseMessage(modelId)), {
          payload: {
            statusCode: "ERR0049",
            message: getEmptyModelResponseMessage(modelId),
          },
        });
      }

      updateChatMessage(messageId, {
        response: answer,
        meta: result.meta,
        responseMeta: buildResponseMeta(result),
        isLoading: false,
        responseType: "success",
        showControlButton: false,
        messageId: result.conversationId,
      });
    } catch (error) {
      const isAborted =
        error?.name === "AbortError" || error?.name === "CanceledError";

      const errorMessage = getAutoCoderErrorMessage(
        error?.payload,
        error?.message || "Something went wrong!"
      );

      const errorCode = error?.payload?.statusCode;

      if (errorCode === "ERR0047") {
        message.destroy();
        message.error(errorMessage);
      }

      updateChatMessage(messageId, {
        response: isAborted
          ? "Response stopped. Click Start to retry."
          : errorMessage,
        isLoading: false,
        responseType: isAborted ? "stopped" : "error",
        showControlButton: true,
      });
    } finally {
      setIsRequestActive(false);
      abortControllerRef.current = null;
      promiseRef.current = null;
    }
  };

  const handleRestart = async () => {
    const messageId = activeMessageIdRef.current;

    if (!messageId || !lastRequestRef.current) return;

    updateChatMessage(messageId, {
      isLoading: true,
      response: "",
      responseMeta: null,
    });

    await executeConversation(lastRequestRef.current, messageId);
  };

  const handleStop = () => {
    abortControllerRef.current?.abort();
    promiseRef.current = null;
  };
  //complete logic of the response render and all validations
  const handleChatSubmit = async (selectedMessage) => {
    const strippedText = selectedMessage.trim();
    const cleanedInput = new DOMParser().parseFromString(strippedText, 'text/html').body.textContent.trim();
    const HTML_TAG_REGEX = /<[^>]+>/;
    const SCRIPT_TAG_REGEX = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/i;
    const lastMessageId = chatHistory[chatHistory.length - 1]?.messageId;
    const lastMessage = chatHistory[chatHistory.length - 1];
    const botProcessing = lastMessage?.isLoading;

    // Check if feedback is required and hasn't been given for the last query
    if (lastMessageId && !feedbackGiven[lastMessageId] && feature.feedbackMandatory) {
      message.error("Please provide feedback for the previous query before submitting a new one.");
      return;
    }
    if (botProcessing) {
      message.error("Please wait for the response to your previous query.");
      return;
    }
    if (!cleanedInput || cleanedInput.length > 5000 || SCRIPT_TAG_REGEX.test(cleanedInput) || HTML_TAG_REGEX.test(cleanedInput)) {
      message.destroy();
      if (!cleanedInput) {
        message.error("Prompt can not be empty.");
      } else if (cleanedInput.length > 5000) {
        message.error("The prompt must be limited to a maximum of 5000 characters.");
      } else if (SCRIPT_TAG_REGEX.test(cleanedInput)) {
        message.error("Invalid input");
      } else if (HTML_TAG_REGEX.test(cleanedInput)) {
        message.error("Please enter a valid prompt without special characters");
      }
      return;
    }

    const fileIds = Array.isArray(selectedFile) ? selectedFile : (selectedFile ? [selectedFile] : []);
    // if (!fileIds.length) {
    //   message.error("Please select at least one file before submitting.");
    //   return;
    // }
    setChatLoad(true);
    const uuid = uuidv4();

    activeMessageIdRef.current = uuid;
    // const currentMessageId = uuid;
    // activeMessageIdRef.current = uuid;

    let currentMessageId = uuid;

    let queryData = {
      type: "user",
      uuid: uuid,
      query: cleanedInput,
      response: null,
      files: [],
      isLoading: true,
      content: cleanedInput,
      responseType: "success",
      showControlButton: true,

    };
    setChatHistory((prevMessages) => [...prevMessages, queryData]);
    let allFileIds = [];

    if (!feature.uploadAccessRole.includes(user.Role)) {
      allFileIds = folderList.reduce((acc, curr) => {
        if (curr.file.length) {
          acc.push(...curr.file.map(file => file.id))
        }
        return acc;
      }, [])
    }
    const requestBody = {
      query: cleanedInput,
      query_id: uuid,
      // ...(feature.uploadFile && {
      //   file_id: feature.uploadAccessRole.includes(user.Role) && (
      //     feature.departmentUploadAccess.includes(user.Department) ||
      //     feature.departmentUploadAccess.includes('all')
      //   ) ? selectedFile : allFileIds
      // }),
      file_id: selectedFile
    };


    lastRequestRef.current = requestBody;
    await executeConversation(requestBody, uuid);
  };

  const getMimeType = (ext) => {
    switch (ext) {
      case '.pdf': return 'application/pdf';
      case '.doc': return 'application/msword';
      case '.docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case '.txt': return 'text/plain';
      default: return 'application/octet-stream';
    }
  };

  const openInViewer = (blob, fileName, fileExtension, page) => {
    const url = URL.createObjectURL(blob);

    if (fileExtension === '.pdf') {
      window.open(`${url}#page=${page}`, "_blank");
    } else if (fileExtension === '.txt') {
      const newWindow = window.open();
      const reader = new FileReader();
      reader.onload = () => {
        newWindow.document.write(`<pre style="white-space: pre-wrap; font-family: monospace;">${reader.result}</pre>`);
      };
      reader.readAsText(blob);
    } else if (fileExtension === '.docx') {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target.result;
        try {
          const result = await mammoth.convertToHtml({ arrayBuffer });
          const newWindow = window.open();
          newWindow.document.write(`
            <html>
              <head>
                <title>DOCX Preview</title>
                <style>
                  body {
                    padding: 20px;
                    font-family: sans-serif;
                    overflow-y: auto;
                  }
                  .page {
                    page-break-before: always;
                    margin-bottom: 20px;
                  }
                </style>
              </head>
              <body>
                <div id="docx-content">${result.value}</div>
                <script>
                  const pages = document.querySelectorAll('.page');
                  if (pages.length > 0) {
                    const pageIndex = ${page} - 1; // Adjusting for 0-based index
                    if (pages[pageIndex]) {
                      pages[pageIndex].scrollIntoView({ behavior: 'smooth' });
                    }
                  }
                </script>
              </body>
            </html>
          `);
          newWindow.document.close();
        } catch (err) {
          console.error("Failed to render DOCX file.", err);
          message.error("Failed to render DOCX file. Please download it manually.");
        }
      };
      reader.readAsArrayBuffer(blob);
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // const downloadMarkDownPdf = (response) => {
  //   const htmlContent = marked(response || "");
  //   // Step 2: Create a temporary container
  //   const container = document.createElement("div");
  //   container.innerHTML = htmlContent;
  //   // Optional styling for PDF
  //   container.style.fontFamily = "Arial, sans-serif";
  //   container.style.fontSize = "12px";
  //   container.style.lineHeight = "1.4";
  //   container.style.color = "#333";

  //   container.innerHTML = container.innerHTML.replace(
  //     /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF]|[\u2600-\u26FF]|[\uFE00-\uFE0F]|[\u200D])/g,
  //     ""
  //   );
  //   const pdf = new jsPDF("p", "mm", "a4");
  //   const margin = 20;
  //   const pdfWidth = pdf.internal.pageSize.getWidth() - margin * 2;

  //   pdf.html(container, {
  //     x: margin,
  //     y: margin,
  //     html2canvas: {
  //       scale: 0.5,
  //       useCORS: true,
  //       backgroundColor: null,
  //     },
  //     width: pdfWidth,
  //     callback: (pdf) => pdf.save("autoCoderResponse.pdf"),
  //   });
  // };
  // parseMarkdownToText.js
  // function parseMarkdownToText(markdown) {
  //   let text = markdown || "";

  //   // Headings (#, ##, ###) → uppercase + spacing
  //   text = text.replace(/^### (.*)$/gm, '$1\n\n');
  //   text = text.replace(/^## (.*)$/gm, '$1\n\n');
  //   text = text.replace(/^# (.*)$/gm, '$1\n\n');

  //   // Bold **text** → keep text
  //   text = text.replace(/\*\*(.+?)\*\*/g, '$1');

  //   // Italic *text* → keep text
  //   text = text.replace(/\*(.+?)\*/g, '$1');

  //   // Inline code `code` → keep code
  //   text = text.replace(/`([^`]+)`/g, '$1');

  //   // Code blocks ```code``` → keep code block with spacing
  //   text = text.replace(/```([\s\S]*?)```/g, function (match, p1) {
  //     return '\n' + p1 + '\n';
  //   });

  //   // Tables: remove | but keep content
  //   text = text.replace(/\|/g, ' | ');

  //   return text;
  // }

  // function downloadMarkdownPdf(markdown, fileName = 'response.pdf') {
  //   const text = parseMarkdownToText(markdown);

  //   const doc = new jsPDF();
  //   const pageWidth = 180; // approximate content width
  //   const lines = doc.splitTextToSize(text, pageWidth);

  //   doc.setFont('Courier'); // monospace for code blocks
  //   doc.setFontSize(12);
  //   doc.text(lines, 10, 10);

  //   doc.save(fileName);
  // }

  const downloadPDF = async (name, fileId, page) => {
    setSelectedPDF(fileId);
    const folder = folderList.find(item => item?.file?.find(file => file.id === fileId));
    const file = folder?.file?.find(item => item?.id === fileId);
    if (!file) return;
    const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!feature.fileFormat.includes(fileExtension) && feature?.uploadFile) {
      message.error(`File format ${fileExtension} is not allowed.`);
      return;
    }
    const data = {
      folderName: folder?.folder_name,
      fileName: file?.name,
      fileId: fileId
    };
    setPDFLoading(true);
    const response = await dispatch(autoCoderChatBotTemplateAPI.getPdf({ serviceId, ...data }));
    if (!response.error) {
      const binaryString = window.atob(response?.payload?.data);
      const binary = new Uint8Array(binaryString?.length);
      for (let i = 0; i < binaryString.length; i++) {
        binary[i] = binaryString.charCodeAt(i);
      }
      const mimeType = getMimeType(fileExtension);
      const blob = new Blob([binary], { type: mimeType });
      openInViewer(blob, file.name, fileExtension, page);
    }
    setPDFLoading(false);
  };

  //logic for the feedback api
  const handleStarClick = async (starRating, messageId) => {
    if (feedbackGiven[messageId]?.starRating >= 4) {
      return; // Prevent editing for 4-5 star feedback
    }
    setRatings((prev) => ({ ...prev, [messageId]: starRating }));
    if (starRating <= 3) {
      const existingFeedback = feedbackGiven[messageId];
      setDislikeModal({
        visible: true,
        messageId,
        starRating,
      });
      // Populate modal with existing feedback
      if (existingFeedback) {
        setSelectedOption(existingFeedback.reason || null);
        setDislikeFeedback(existingFeedback.comments || "");
      } else {
        setSelectedOption(null);
        setDislikeFeedback("");
      }
    } else {
      try {
        // Save feedback directly for ratings 4 and 5
        const feedbackData = { messageId, feedbackType: starRating };
        dispatch(autoCoderChatBotTemplateAPI.saveFeedback({ serviceId, ...feedbackData }));
        setFeedbackGiven((prev) => ({
          ...prev,
          [messageId]: { starRating },
        }));
        message.success("Feedback submitted successfully!", 3);
      } catch (err) {
        //console.log(err, "err in feedback api");
      }
    }
  };

  const handleModalClose = () => {
    const existingFeedback = feedbackGiven[dislikeModal.messageId];
    if (existingFeedback) {
      // Restore previous feedback (if any)
      setRatings((prev) => ({
        ...prev,
        [dislikeModal.messageId]: existingFeedback.starRating,
      }));
      setSelectedOption(existingFeedback.reason || null);
      setDislikeFeedback(existingFeedback.comments || "");
    } else {
      // Reset the star rating to undefined (if no existing feedback)
      setRatings((prev) => ({
        ...prev,
        [dislikeModal.messageId]: undefined,
      }));
      setSelectedOption(null);
      setDislikeFeedback("");
    }
    // Close the modal
    setDislikeModal({ visible: false, messageId: null, starRating: null });
  };

  const handleModalSubmit = async () => {
    if (!selectedOption) {
      message.error("Please select a reason for your dislike.");
      return;
    }
    const feedbackMessage = `${selectedOption}${dislikeFeedback ? `. ${dislikeFeedback}` : ""
      }`;
    const feedbackData = {
      messageId: dislikeModal.messageId,
      feedback: feedbackMessage,
      feedbackType: dislikeModal?.starRating
    };
    await dispatch(autoCoderChatBotTemplateAPI.saveFeedback({ serviceId, ...feedbackData }));
    setFeedbackGiven((prev) => ({
      ...prev,
      [dislikeModal.messageId]: {
        starRating: dislikeModal.starRating,
        reason: selectedOption,
        comments: dislikeFeedback,
      },
    }));
    message.success("Feedback updated successfully!", 3);
    // Reset modal state
    setDislikeModal({ visible: false, messageId: null });
    setSelectedOption(null);
    setDislikeFeedback("");
  };

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const handleDislikeInputChange = (e) => {
    setDislikeFeedback(e.target.value);
  };

  const renderResponseMeta = (responseMeta) => {
    if (!feature.responseMetadata || !responseMeta) return null;

    const hasTokenUsage =
      responseMeta.totalTokens != null ||
      responseMeta.promptTokens != null ||
      responseMeta.completionTokens != null;

    const latencyMs = responseMeta.timeTakenMs ?? responseMeta.latencyMs;

    return (
      <div className="auto-coder-response-meta">
        <span >
          <InfoCircleOutlined /> <strong>Model:</strong> {responseMeta.modelLabel || responseMeta.model}
        </span>
        <span style={{ marginLeft: "2px" }}><strong>Role:</strong> {responseMeta.roleLabel}</span>
        {latencyMs != null && (
          <span><strong>Time:</strong> {(latencyMs / 1000).toFixed(2)}s</span>
        )}
        {hasTokenUsage && (
          <span>
            <strong>Tokens:</strong> Prompt({responseMeta.promptTokens ?? "—"}) + Gen(
            {responseMeta.completionTokens ?? "—"}) = Total(
            {responseMeta.totalTokens ?? "—"})
          </span>
        )}
      </div>
    );
  };

  const markdownComponents = useMemo(() => ({
    h3: ({ node, ...props }) => (
      <h3 style={{ fontSize: "14px" }} {...props} />
    ),

    code({ node, inline, className, children, ...props }) {
      if (!inline) {
        const match = /language-(\w+)/.exec(className || "");
        const language = match ? match[1] : "";

        const codeString = String(children).replace(/\n$/, "");

        return (
          <div style={{ position: "relative", marginBottom: "1em" }}>
            <SyntaxHighlighter
              language={language}
              style={atomDark}
              PreTag="div"
              {...props}
            >
              {codeString}
            </SyntaxHighlighter>

            <button
              onClick={() => navigator.clipboard.writeText(codeString)}
              style={{
                position: "absolute",
                top: "5px",
                right: "5px",
                padding: "4px 8px",
                background: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "3px",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Copy
            </button>
          </div>
        );
      }

      return <code>{children}</code>;
    },
  }), []);

  const downloadBase64File = ({ blob_string, filename, media_type }) => {
    try {
      const byteCharacters = atob(blob_string);

      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: media_type });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error("Download failed:", error);
      return false;
    }
  };

  const handleDownload = async (type, response, id) => {
    const downloadFileData = {
      type,
      markDownString: response,
      msg_id:id
    };    

    try {
      const result = await dispatch(
        autoCoderChatBotTemplateAPI.downloadFile({
          serviceId,
          ...downloadFileData,
        })
      ).unwrap();

      const fileData = result?.data;

      if (
        !fileData?.blob_string ||
        !fileData?.filename ||
        !fileData?.media_type
      ) {
        message.error("Currently file is not downloaded");
        return;
      }

      const downloaded = downloadBase64File(fileData);

      if (downloaded) {
        message.success(
          `${type.toUpperCase()} file downloaded successfully!`
        );
      } else {
        message.error("Currently file is not downloaded");
      }
    } catch (error) {
      console.error(error);
      message.error("Currently file is not downloaded");
    }
  };

  return (
    <>
      <div
        ref={chatContentRef}
        className="chat-main-section"
        style={styles.conversation}
      >
        {chatHistory.length > 0
          ? chatHistory.map((item, index) => {
            console.log(item,"+++++++++");
            
            return (
              <div
                key={index}
                className="chat-main-part"
                ref={(el) => (messagesRef.current[index] = el)}
              >
                <div
                  className={`chat-message user`}
                  style={{ alignItems: "flex-start" }}
                >
                  <span className="user-message success">{item.query}</span>
                  <img
                    className="user-icon"
                    style={{ marginTop: "0" }}
                    src={UserIcon}
                    alt="user-icon"
                    width={40}
                    height={40}
                  />
                </div>
                <div
                  className={`chat-message bot`}
                  style={{ alignItems: "flex-start" }}

                >
                  <img
                    className="bot-icon"
                    src={BotIcon}
                    alt="bot-icon"
                    width="auto"
                    height={40}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}>
                    {item.isLoading ? (
                      <Spin
                        size="small"
                        style={{ marginLeft: 8, marginTop: 11 }}
                      />
                    ) : (
                      <>
                        <div className={`bot-message markdown-wrapper-gpt ${item?.responseType}`}
                          ref={(el) => { markdownRefs.current[item.id] = el; }}
                        >
                          <SafeMarkdown
                            content={item?.response}
                            components={markdownComponents}
                          />
                          {renderResponseMeta(item?.responseMeta)}
                          {feature?.fileReference && (
                            <>
                              {
                                item?.meta && (
                                  <>
                                    {item?.meta?.length > 0 && (
                                      <div
                                        className="ai-generated-text"
                                      >
                                        This response is generated by Gen AI. Please
                                        verify the information by clicking on the given
                                        pdf link.
                                      </div>
                                    )}

                                    <div
                                      className="meta-tag-div"
                                    >
                                      <div style={{ display: "flex", flexDirection: "column" }}>
                                        {item?.meta?.map((file, index) => {
                                          return (
                                            <div key={index} className="meta-details">
                                              <div className="meta-details-innerdiv">
                                                <div className="file-pageno" onClick={() => downloadPDF(getFileName(file?.file_id), file?.file_id, 0)}>
                                                  <FilePdfOutlined />
                                                  {getFileName(file?.file_id)}
                                                </div>

                                                {file?.page.length > 0 && (
                                                  <div>
                                                    &nbsp;- (
                                                    {file?.page.map((page, index) => (
                                                      <span
                                                        key={index}
                                                        onClick={() =>
                                                          downloadPDF(
                                                            getFileName(file?.file_id),
                                                            file?.file_id,
                                                            page
                                                          )
                                                        }
                                                        className="meta-pages-no"
                                                      >
                                                        Page no: {page}
                                                        {index !==
                                                          file?.page.length - 1 && ", "}
                                                      </span>
                                                    ))}
                                                    )
                                                  </div>
                                                )}
                                              </div>
                                              {selectedPDF === file?.file_id &&
                                                pdfLoading && <div className="text-loading" >Loading...</div>}
                                            </div>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  </>
                                )
                              }
                            </>
                          )
                          }
                          {
                            // feature.feedbackMandatory &&
                            <div>
                              {item.responseType !== "error" && (
                                <div
                                  className="star-rating"
                                  style={{
                                    display: "flex",
                                    gap: "2px",
                                    float: "right",
                                    marginRight: "10px",
                                    alignItems: "end",
                                  }}
                                >
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                      key={star}
                                      onClick={() =>
                                        feedbackGiven[item.messageId]
                                          ?.starRating >= 4
                                          ? null
                                          : handleStarClick(
                                            star,
                                            item.messageId
                                          )
                                      } // Block interaction for 4-5 ratings
                                      className={`star ${ratings[item.messageId] >= star
                                        ? "filled"
                                        : ""
                                        }`}
                                      style={{
                                        cursor:
                                          feedbackGiven[item.messageId]
                                            ?.starRating >= 4
                                            ? "not-allowed"
                                            : "pointer", // Show blocked cursor for 4-5
                                      }}
                                    >
                                      <StarOutlined />
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          }
                          {
                            item.responseType !== "error" && (
                              <div className="copy-icon-wrapper">
                                <Tooltip title="Copy response">
                                  <CopyOutlined
                                    className="copy-icon"
                                    onClick={() => {
                                      navigator.clipboard.writeText(item?.response);
                                      message.success("Response copied to clipboard!");
                                    }}
                                  />
                                </Tooltip>
                                {/* <Tooltip title="Download Response">
                                  <DownloadOutlined
                                    className="copy-icon"
                                    onClick={() =>
                                      downloadMarkdownPdf(item.response, `response_${item.id}.pdf`)
                                    }

                                  />
                                </Tooltip> */}

                                <Dropdown
                                  menu={{
                                    items: [
                                      {
                                        key: "pdf",
                                        label: "Download as PDF",
                                        onClick: () => handleDownload("pdf", item.response, item.messageId),
                                      },
                                      {
                                        key: "docx",
                                        label: "Download as DOCX",
                                        onClick: () => handleDownload("docx", item.response, item.messageId),
                                      },
                                    ],
                                  }}
                                  trigger={["click"]}
                                >
                                  <Tooltip title="Download Response">
                                    <DownloadOutlined className="copy-icon" />
                                  </Tooltip>
                                </Dropdown>

                              </div>
                            )
                          }

                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

            )
          })
          : null}
      </div>

      {/* <button
        onClick={() => {
          if (isRequestActive) {
            handleStop();
          } else {
            handleRestart();
          }
        }}
      >
        {isRequestActive ? "Stop" : "Start"}
      </button> */}

      <Modal
        title="Tell us more, why did you dislike?"
        open={dislikeModal.visible}
        onCancel={handleModalClose}
        onOk={handleModalSubmit}
        okText="Submit"
        closable={false} // Removes the close icon
        maskClosable={false} // Prevents closing when clicking outside the modal
        okButtonProps={{
          disabled: !selectedOption, // Enable the submit button only if a radio button is selected
        }}
      >
        <Radio.Group onChange={handleOptionChange} value={selectedOption}>
          <Radio value="Relevant but not exhaustive">
            Relevant but not exhaustive
          </Radio>
          <Radio value="Slightly irrelevant">Slightly irrelevant </Radio>
          <Radio value="Completely irrelevant">Completely irrelevant</Radio>
        </Radio.Group>
        <TextArea
          ref={dislikeInputRef}
          placeholder="Please provide additional comments (optional)"
          value={dislikeFeedback}
          onChange={handleDislikeInputChange}
          autoFocus
          style={{
            height: "6rem",
            borderRadius: "5px",
            padding: "0.5rem",
            fontSize: "14px",
            boxSizing: "border-box",
            lineHeight: "1.25",
            verticalAlign: "top",
            display: "block",
            width: "100%",
            resize: "none",
          }}
          maxLength={1000}
        />
      </Modal>
    </>
  );
};

export default AutoCoderChatSection;

const styles = {
  conversation: {
    flex: 1,
    overflowY: "overlay",
    // maxWidth: "923px",
    marginLeft: "auto",
    marginRight: "auto",
    width: "100%",
    paddingTop: "5px",
    paddingBottom: "5px",
    marginTop: "10px",
    marginBottom: "10px"
  }
};